import { useCallback, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { DashboardOutlined, RefreshOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { usePlatformSummaryQuery } from '@/state/queries/ops-admin/use-platform-summary';
import { useAttentionQueueQuery } from '@/state/queries/ops-admin/use-attention-queue';
import { useAgentPoolsLiveSync } from '@/hooks/use-agent-pools-live-sync';
import {
  useOpsReleaseProcessorMutation,
  useOpsResetProcessorMutation,
  useOpsRequeuePreprocessMutation,
  useOpsRetryFailedPreprocessMutation,
  useOpsResetMediaAgentBookingMutation,
} from '@/state/mutations/ops-admin/use-ops-actions';
import {
  useRecoverMediaAgentMutation,
  useReissueMediaAgentTokenMutation,
} from '@/state/mutations/media-agent-admin/use-media-agent-recovery-mutations';
import {
  useRecoverProcessorMutation,
  useReissueProcessorTokenMutation,
} from '@/state/mutations/processor-admin/use-processor-recovery-mutations';
import { useNotifications } from '@/hooks/use-notifications';
import { agentPoolsTabPath } from '@/components/agent-pools-admin/agent-pools-tabs';
import {
  MEDIA_AGENT_HEAL_ATTENTION_TYPES,
  PROCESSOR_HEAL_ATTENTION_TYPES,
  type OpsAttentionRowDto,
} from '@/types/dtos/ops/ops-platform.dto';
import OpsAttentionTable from './ops-attention-table';
import ProcessorDomainPanel from './processor-domain-panel';
import SingleCamDomainPanel from './singlecam-domain-panel';
import MediaAgentDomainPanel from './media-agent-domain-panel';
import ProcessorThroughputTable from './processor-throughput-table';
import { copyText, formatCollectedAt, formatMediaRef } from './ops-format';

type PendingOpsConfirm = { kind: 'row'; row: OpsAttentionRowDto } | { kind: 'resetProcessor'; playerId: string };

const HEALTH_COLOR = {
  healthy: 'success',
  degraded: 'warning',
  critical: 'error',
} as const;

function needsConnectedProcessor(action: OpsAttentionRowDto['primaryAction']['type']): boolean {
  return action === 'retry_failed' || action === 'requeue';
}

function isNavigationAction(type: OpsAttentionRowDto['primaryAction']['type']): boolean {
  return (
    type === 'prepare_processor' ||
    type === 'activate_vm' ||
    type === 'prepare_singlecam_agent' ||
    type === 'prepare_media_agent'
  );
}

/**
 * A standalone shell with a dead token cannot be healed from the backend — the operator has to
 * re-pair the device, so the row deep-links into the pool instead of firing a mutation.
 */
function isMediaAgentPairHandoff(row: OpsAttentionRowDto): boolean {
  return row.type === MEDIA_AGENT_HEAL_ATTENTION_TYPES.NEEDS_REPAIR && row.primaryAction.type !== 'recover_media_agent';
}

/**
 * A stale processor token cannot be healed from the backend either — only `recover_processor`
 * still runs as a mutation, everything else deep-links into the pool.
 */
function isProcessorPairHandoff(row: OpsAttentionRowDto): boolean {
  return row.type === PROCESSOR_HEAL_ATTENTION_TYPES.NEEDS_REPAIR && row.primaryAction.type !== 'recover_processor';
}

function isPairHandoff(row: OpsAttentionRowDto): boolean {
  return isMediaAgentPairHandoff(row) || isProcessorPairHandoff(row);
}

export default function OpsAdminPage() {
  const t = useTranslations();
  const notification = useNotifications();
  useAgentPoolsLiveSync(true);
  const [pendingConfirm, setPendingConfirm] = useState<PendingOpsConfirm | null>(null);
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
    isFetching,
  } = usePlatformSummaryQuery();
  const {
    data: attention,
    isLoading: attentionLoading,
    error: attentionError,
    refetch: refetchAttention,
  } = useAttentionQueueQuery();

  const { mutateAsync: releaseProcessor, isPending: isReleasing } = useOpsReleaseProcessorMutation();
  const { mutateAsync: resetProcessor, isPending: isResetting } = useOpsResetProcessorMutation();
  const { mutateAsync: requeuePreprocess, isPending: isRequeuing } = useOpsRequeuePreprocessMutation();
  const { mutateAsync: retryFailed, isPending: isRetrying } = useOpsRetryFailedPreprocessMutation();
  const { mutateAsync: resetMediaAgentBooking, isPending: isResettingBooking } = useOpsResetMediaAgentBookingMutation();
  const { mutateAsync: reissueMediaAgentToken, isPending: isReissuingToken } = useReissueMediaAgentTokenMutation();
  const { mutateAsync: recoverMediaAgent, isPending: isRecoveringMediaAgent } = useRecoverMediaAgentMutation();
  const { mutateAsync: reissueProcessorToken, isPending: isReissuingProcessorToken } =
    useReissueProcessorTokenMutation();
  const { mutateAsync: recoverProcessor, isPending: isRecoveringProcessor } = useRecoverProcessorMutation();

  const processor = summary?.domains.processor;
  const singlecam = summary?.domains.singlecam;
  const mediaAgent = summary?.domains.mediaAgent;
  const processorExhaustion = processor?.exhaustion;
  const singlecamExhaustion = singlecam?.exhaustion;
  const mediaAgentExhaustion = mediaAgent?.exhaustion;
  const statistics = processor?.statistics;
  const processorsAvailable = processor?.enabled === true && (processorExhaustion?.connected ?? 0) > 0;
  const isActionPending =
    isReleasing ||
    isResetting ||
    isRequeuing ||
    isRetrying ||
    isResettingBooking ||
    isReissuingToken ||
    isRecoveringMediaAgent ||
    isReissuingProcessorToken ||
    isRecoveringProcessor;

  const handleCopyMediaRef = useCallback(
    async (row: OpsAttentionRowDto) => {
      const { mediaId, assetVersion } = row.context;
      if (!mediaId || assetVersion == null) {
        return;
      }
      try {
        await copyText(formatMediaRef(mediaId, assetVersion));
        notification.notifySuccess(t('admin.opsAdmin.copiedMediaRef', undefined, 'Copied media reference'));
      } catch {
        notification.notifyError(t('admin.opsAdmin.copyError', undefined, 'Failed to copy'));
      }
    },
    [notification, t],
  );

  const handleRefresh = useCallback(() => {
    void refetchSummary();
    void refetchAttention();
  }, [refetchAttention, refetchSummary]);

  const executeResetProcessor = useCallback(
    async (playerId: string) => {
      try {
        const result = await resetProcessor(playerId);
        const message = t(
          'admin.opsAdmin.resetProcessorSuccess',
          undefined,
          'Processor reset — requeued {count} job(s)',
        ).replace('{count}', String(result.requeued));
        const suffix = !result.resetEmitted ? ' (agent offline — restart manually if needed)' : '';
        if (result.warnings?.length) {
          notification.notifySuccess(`${message}${suffix} (${result.warnings.join('; ')})`);
        } else {
          notification.notifySuccess(`${message}${suffix}`);
        }
      } catch {
        notification.notifyError(t('admin.opsAdmin.resetProcessorError', undefined, 'Failed to reset processor'));
      }
    },
    [notification, resetProcessor, t],
  );

  const executeRowAction = useCallback(
    async (row: OpsAttentionRowDto) => {
      if (needsConnectedProcessor(row.primaryAction.type) && !processorsAvailable) {
        notification.notifyError(
          t(
            'admin.opsAdmin.actionNeedsProcessor',
            undefined,
            'No connected processors — prepare or activate one first',
          ),
        );
        return;
      }
      if (isMediaAgentPairHandoff(row)) {
        window.location.assign(row.deepLink || agentPoolsTabPath('media-agent'));
        return;
      }
      if (isProcessorPairHandoff(row)) {
        window.location.assign(row.deepLink || agentPoolsTabPath('processor'));
        return;
      }
      try {
        switch (row.primaryAction.type) {
          case 'retry_failed': {
            if (!row.context.mediaId || row.context.assetVersion == null) {
              return;
            }
            const result = await retryFailed({
              mediaId: row.context.mediaId,
              assetVersion: row.context.assetVersion,
            });
            notification.notifySuccess(`Retry: ${result.result}`);
            break;
          }
          case 'requeue': {
            if (!row.context.mediaId || row.context.assetVersion == null) {
              return;
            }
            const result = await requeuePreprocess({
              mediaId: row.context.mediaId,
              assetVersion: row.context.assetVersion,
            });
            notification.notifySuccess(`Requeue: ${result.result}`);
            break;
          }
          case 'release_processor': {
            if (!row.context.playerId) {
              return;
            }
            const result = await releaseProcessor(row.context.playerId);
            notification.notifySuccess(`Released — requeued ${result.requeued} job(s)`);
            break;
          }
          case 'reset_processor': {
            if (!row.context.playerId) {
              return;
            }
            await executeResetProcessor(row.context.playerId);
            break;
          }
          case 'prepare_processor':
          case 'activate_vm':
            window.location.assign(row.deepLink || agentPoolsTabPath('processor'));
            return;
          case 'prepare_singlecam_agent':
            window.location.assign(row.deepLink || agentPoolsTabPath('singlecam'));
            return;
          case 'prepare_media_agent':
            window.location.assign(row.deepLink || agentPoolsTabPath('media-agent'));
            return;
          case 'reset_media_agent_booking': {
            if (!row.context.bookingId) {
              notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
              return;
            }
            const result = await resetMediaAgentBooking(row.context.bookingId);
            notification.notifySuccess(
              t(
                'admin.opsAdmin.resetBookingSuccess',
                { status: result.status },
                `Booking reset — status ${result.status}`,
              ),
            );
            break;
          }
          case 'reissue_media_agent_token': {
            if (!row.context.playerId) {
              notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
              return;
            }
            if (row.type === MEDIA_AGENT_HEAL_ATTENTION_TYPES.NEEDS_REPAIR) {
              window.location.assign(row.deepLink || agentPoolsTabPath('media-agent'));
              return;
            }
            await reissueMediaAgentToken(row.context.playerId);
            notification.notifySuccess(
              t(
                'admin.opsAdmin.reissueMediaAgentTokenSuccess',
                undefined,
                'Fresh token re-injected — shell is reconnecting',
              ),
            );
            break;
          }
          case 'recover_media_agent': {
            if (!row.context.playerId) {
              notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
              return;
            }
            await recoverMediaAgent({
              playerId: row.context.playerId,
              mode: row.context.recoverMode ?? 'auto',
            });
            notification.notifySuccess(
              t('admin.opsAdmin.recoverMediaAgentSuccess', undefined, 'Media agent recovery started'),
            );
            break;
          }
          case 'reissue_processor_token': {
            if (!row.context.playerId) {
              notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
              return;
            }
            if (row.type === PROCESSOR_HEAL_ATTENTION_TYPES.NEEDS_REPAIR) {
              window.location.assign(row.deepLink || agentPoolsTabPath('processor'));
              return;
            }
            await reissueProcessorToken(row.context.playerId);
            notification.notifySuccess(
              t('admin.opsAdmin.reissueProcessorTokenSuccess', undefined, 'Processor token reissued'),
            );
            break;
          }
          case 'recover_processor': {
            if (!row.context.playerId) {
              notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
              return;
            }
            await recoverProcessor({
              playerId: row.context.playerId,
              mode: row.context.recoverMode ?? 'auto',
            });
            notification.notifySuccess(
              t('admin.opsAdmin.recoverProcessorSuccess', undefined, 'Processor recovery started'),
            );
            break;
          }
        }
      } catch {
        notification.notifyError(t('admin.opsAdmin.actionError', undefined, 'Action failed'));
      }
    },
    [
      executeResetProcessor,
      notification,
      processorsAvailable,
      recoverMediaAgent,
      recoverProcessor,
      reissueMediaAgentToken,
      reissueProcessorToken,
      requeuePreprocess,
      releaseProcessor,
      resetMediaAgentBooking,
      retryFailed,
      t,
    ],
  );

  const requestRowAction = useCallback(
    (row: OpsAttentionRowDto) => {
      if (isNavigationAction(row.primaryAction.type) || isPairHandoff(row)) {
        void executeRowAction(row);
        return;
      }
      if (needsConnectedProcessor(row.primaryAction.type) && !processorsAvailable) {
        notification.notifyError(
          t(
            'admin.opsAdmin.actionNeedsProcessor',
            undefined,
            'No connected processors — prepare or activate one first',
          ),
        );
        return;
      }
      setPendingConfirm({ kind: 'row', row });
    },
    [executeRowAction, notification, processorsAvailable, t],
  );

  const requestResetProcessor = useCallback((playerId: string) => {
    setPendingConfirm({ kind: 'resetProcessor', playerId });
  }, []);

  const confirmTitle =
    pendingConfirm?.kind === 'resetProcessor'
      ? t('admin.opsAdmin.resetProcessorConfirmTitle', undefined, 'Reset media processor?')
      : pendingConfirm?.kind === 'row'
        ? t(
            'admin.opsAdmin.attentionConfirmTitle',
            { action: pendingConfirm.row.primaryAction.label },
            `Confirm: ${pendingConfirm.row.primaryAction.label}?`,
          )
        : '';

  const confirmText =
    pendingConfirm?.kind === 'resetProcessor'
      ? t(
          'admin.opsAdmin.resetProcessorConfirmText',
          { playerId: pendingConfirm.playerId },
          `Reset processor ${pendingConfirm.playerId}? This releases any in-flight job, requeues it, and restarts the agent process on the VM.`,
        )
      : pendingConfirm?.kind === 'row'
        ? t(
            'admin.opsAdmin.attentionConfirmText',
            { summary: pendingConfirm.row.summary, action: pendingConfirm.row.primaryAction.label },
            `${pendingConfirm.row.summary} — Run “${pendingConfirm.row.primaryAction.label}”? This may interrupt in-flight work.`,
          )
        : '';

  const confirmLabel =
    pendingConfirm?.kind === 'resetProcessor'
      ? t('admin.opsAdmin.attentionTable.reset', undefined, 'Reset')
      : pendingConfirm?.kind === 'row'
        ? pendingConfirm.row.primaryAction.label
        : t('common.confirm', undefined, 'Confirm');

  const handleConfirmPending = async () => {
    if (!pendingConfirm) return;
    try {
      if (pendingConfirm.kind === 'resetProcessor') {
        await executeResetProcessor(pendingConfirm.playerId);
      } else {
        await executeRowAction(pendingConfirm.row);
      }
    } finally {
      setPendingConfirm(null);
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column', pb: 2 }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <DashboardOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.opsAdmin.title', undefined, 'Platform Ops')}
                  </Typography>
                  {summary && (
                    <>
                      <Chip
                        size="small"
                        label={summary.health.status}
                        color={HEALTH_COLOR[summary.health.status]}
                        variant="outlined"
                      />
                      {summary.health.activeIncidents > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {t(
                            'admin.opsAdmin.activeIncidents',
                            { count: summary.health.activeIncidents },
                            `${summary.health.activeIncidents} active incident(s)`,
                          )}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          'admin.opsAdmin.lastUpdated',
                          { time: formatCollectedAt(summary.collectedAt) },
                          `Updated ${formatCollectedAt(summary.collectedAt)}`,
                        )}
                      </Typography>
                    </>
                  )}
                </Box>
                <Tooltip title={t('admin.opsAdmin.refresh', undefined, 'Refresh')}>
                  <span>
                    <IconButton onClick={handleRefresh} disabled={isFetching} size="small">
                      {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            }
          />

          {(summaryError || attentionError) && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {t('admin.opsAdmin.error', undefined, 'Failed to load ops dashboard')}
            </Alert>
          )}

          {!processor?.enabled && !summaryLoading && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t(
                'admin.opsAdmin.processorDisabled',
                undefined,
                'Media processor feature is disabled on this environment.',
              )}
            </Alert>
          )}

          {!singlecam?.enabled && !summaryLoading && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t(
                'admin.opsAdmin.singlecamDisabled',
                undefined,
                'Single-cam agent pool is disabled on this environment.',
              )}
            </Alert>
          )}

          {!mediaAgent?.enabled && !summaryLoading && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t('admin.opsAdmin.mediaAgentDisabled', undefined, 'Media agent pool is disabled on this environment.')}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.75 }}>
            {t('admin.opsAdmin.attentionTitle', undefined, 'Attention queue')}
            {attention?.rows.length ? ` (${attention.rows.length})` : ''}
          </Typography>

          <OpsAttentionTable
            rows={attention?.rows ?? []}
            loading={attentionLoading}
            processorsAvailable={processorsAvailable}
            isActionPending={isActionPending}
            onRowAction={requestRowAction}
            onResetProcessor={requestResetProcessor}
            onCopyMediaRef={(row) => void handleCopyMediaRef(row)}
          />

          <ConfirmationModal
            open={pendingConfirm !== null}
            title={confirmTitle}
            text={confirmText}
            confirmLabel={confirmLabel}
            isLoading={isActionPending}
            onCancel={() => setPendingConfirm(null)}
            onConfirm={() => void handleConfirmPending()}
          />

          {summaryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 2 }}>
              {processorExhaustion && processor?.enabled && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <ProcessorDomainPanel exhaustion={processorExhaustion} health={processor.health} />
                </Grid>
              )}
              {singlecamExhaustion && singlecam?.enabled && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <SingleCamDomainPanel exhaustion={singlecamExhaustion} health={singlecam.health} />
                </Grid>
              )}
              {mediaAgentExhaustion && mediaAgent?.enabled && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <MediaAgentDomainPanel
                    exhaustion={mediaAgentExhaustion}
                    health={mediaAgent.health}
                    demandTimelineFallback={mediaAgent.statistics?.bookings.demandTimeline48h}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {statistics && processor?.enabled && <ProcessorThroughputTable statistics={statistics} />}
        </Box>
      }
    />
  );
}
